<script setup lang="ts">
import { computed, onMounted, onUnmounted, PropType, ref, watch } from "vue";
import { Address, Distributor, Recipients, Trainer } from "../../types/PersologOnlineTypes.js";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../../api/index.ts";
import { useI18n } from "vue-i18n";
import {
    autofocusFix,
    formatMonthPeriod,
    getAllTimeZones,
    getCurrentTimeZone,
    getLanguage,
    getRecipientName,
    getSafeRedirect,
    getTrainerDetails,
    isMobileView,
    isNewAccount,
    normalizeWebsiteUrl,
    parseMonthPeriod
} from "../../utils/GeneralUtils.ts";
import { CircleStencil, Cropper, Preview } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import UserAvatar from "@persolog/ui/UserAvatar.vue";
import Image from "primevue/image";
import { useConfirm } from "primevue/useconfirm";
import AccountDeletion from "./AccountDeletion.vue";
import TooltipButton from "../common/TooltipButton.vue";
import { keycloak, trainerDetails } from "../../main.ts";

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true
    }
});

const emit = defineEmits(["update:visible", "update:picture"]);

const { t } = useI18n();
const confirm = useConfirm();
const distributors = ref<Distributor[]>();
const languages = ref<{ name: string; value: string }[]>([]);
const visible = ref(props.visibleToggle);
const loading = ref(false);

const trainer = ref(props.trainer);
const fullname = computed(() => {
    return `${trainer.value?.firstname} ${trainer.value?.lastname}`;
});
const firstname = ref<string>();
const lastname = ref<string>();
const email = ref<string>();
const street = ref<string>();
const zipCode = ref<string>();
const city = ref<string>();
const country = ref<string>();
const website = ref<string>();
const websiteFocused = ref(false);
const phoneNo = ref<string>();
const company = ref<string>();
const recipient = ref<string>();
const distributor = ref<Distributor>(props.trainer.distributor);
const timeZone = ref<string>(getCurrentTimeZone());
const distributorAccount = ref<boolean>(false);
const isMonthlyBillingEnabled = ref<boolean>(false);
/** Newly registered trainers will have monthly billing enabled. Distributor only property. */
const isMonthlyBillingEnabledByDefault = ref<boolean>(false);
/**
 * Used so a save only writes the shared distributor flag when the toggle actually changed — otherwise one
 * distributor account saving unrelated settings would silently overwrite the value another
 * distributor account (sharing the same distributor) had set.
 */
const initialMonthlyBillingEnabledByDefault = ref<boolean>(false);

const imageUrl = ref();
const logoUrl = ref();

// Baselines captured when the dialog opens and after a successful save. The
// portrait cropper and logo menu now only stage changes locally, so unsaved
// picture/logo changes are detected by comparing the current values against
// these snapshots.
const initialPortrait = ref();
const initialLogo = ref();
const hasUnsavedImageChanges = computed(
    () => imageUrl.value !== initialPortrait.value || logoUrl.value !== initialLogo.value
);
const showUnsavedConfirm = ref(false);

const standardParticipantRetentionPeriod = ref<number>(
    parseMonthPeriod(props.trainer?.standardRetentionPeriod?.toString()) ?? 24
);
const standardParticipantDeletionNotification = ref<boolean>(props.trainer?.deletionNotification ?? true);

const distributorInfoText = computed(() => {
    return t("header.accountSettings.distributor.info", {
        shoplink:
            "<a href='https://shop.persolog.com/auth/redirect/login?redirectTo=frontend.account.home.page&utm_source=persolog+online&utm_medium=anzeige' target='_blank'>shop.persolog.com</a>",
        supportemail: "<a href='mailto:support@persolog.com'>support@persolog.com</a>"
    });
});

async function getDistributors(): Promise<Distributor[]> {
    return await ProfileAdministrationAPI.fetchDistributors();
}

const recipients = computed(() => {
    return Object.values(Recipients).map((recipient: Recipients) => ({
        name: getRecipientName(props.trainer, recipient),
        value: recipient
    }));
});

const timezones = computed(() => {
    return getAllTimeZones();
});

const selectedRecipient = ref();
const selectedLanguage = ref();

const cropOverlay = ref();

// Hidden native file inputs triggered by the preview placeholders/tiles.
const logoFileInput = ref<HTMLInputElement>();
const portraitFileInput = ref<HTMLInputElement>();
// Mobile view doesn't render the preview tiles (and their inputs), so it uses its own header-level input.
const portraitFileInputMobile = ref<HTMLInputElement>();

const triggerLogoSelect = () => logoFileInput.value?.click();
const triggerPortraitSelect = () => (portraitFileInput.value ?? portraitFileInputMobile.value)?.click();

const onPortraitFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
        const reader = new FileReader();
        reader.onloadend = (e: ProgressEvent<FileReader>) => {
            imageUrl.value = e.target?.result?.toString() ?? "";
            // Mobile has no cropper popover; the raw image is used as-is.
            if (isMobileView()) return;
            // show avatar cropper anchored to the header avatar preview
            cropOverlay.value.show(event, globalThis.document.getElementById("userAvatarPreview"));
        };
        reader.readAsDataURL(file);
    }
    // allow selecting the same file again
    input.value = "";
};

const onLogoFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            logoUrl.value = typeof e.target?.result === "string" ? e.target.result : "";
        };
        reader.readAsDataURL(file);
    }
    // allow selecting the same file again
    input.value = "";
};

interface CropObject {
    canvas: HTMLCanvasElement;
    image: HTMLImageElement;
    imageSrc: string;
    coordinates: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
}

const croppedImage = ref<CropObject | null>();
const croppedImageCoordinates = ref();

const onCropChanged = (cropObject: CropObject) => {
    croppedImage.value = cropObject;
    croppedImageCoordinates.value = cropObject.coordinates;
    parseCroppedImage();
};

const resetImageCrop = (full: boolean = false) => {
    croppedImage.value = null;
    croppedImageCoordinates.value = null;
    if (full) imageUrl.value = props.trainer.portrait ?? "";
};

const onCropConfirm = () => {
    // Stage the cropped image locally. It is persisted only when the user saves
    // the whole AccountSettings dialog via the main Save button.
    if (croppedImage.value) {
        imageUrl.value = croppedImage.value.imageSrc;
    }
    croppedImage.value = null;
    croppedImageCoordinates.value = null;
    cropOverlay.value?.hide();
};

const onCropCancel = () => {
    // Reset to original state
    resetImageCrop(true);
    cropOverlay.value?.hide();
};

// Open the cropper edit box for the already existing profile picture.
const editPortraitClicked = (event: Event) => {
    cropOverlay.value.show(event, globalThis.document.getElementById("userAvatarPreview"));
};

// Delete the profile picture from within the cropper edit box.
const onCropDelete = () => {
    // Stage the removal locally; persisted on the main Save button.
    resetImageCrop();
    imageUrl.value = "";
    cropOverlay.value?.hide();
};

const parseCroppedImage = () => {
    if (croppedImage.value) croppedImage.value.imageSrc = croppedImage.value.canvas.toDataURL();
};

// avatar
const hasPortrait = computed(() => !!(trainer.value.portrait || (imageUrl.value && imageUrl.value !== "")));

// Mobile has no cropper popover, so the avatar offers a simple menu: choose a new image or remove the current one.
const mobileAvatarMenu = ref();
const mobileAvatarOptions = computed(() => {
    const options = [
        {
            label: () => t("header.accountSettings.chooseProfileImage"),
            icon: "pi pi-image",
            command: () => triggerPortraitSelect()
        }
    ];
    if (hasPortrait.value) {
        options.push({
            label: () => t("header.accountSettings.removeProfileImage"),
            icon: "pi pi-trash",
            command: () => confirmRemovePortrait()
        });
    }
    return options;
});

const confirmRemovePortrait = () => {
    confirm.require({
        group: "removePortrait",
        header: t("header.accountSettings.removeProfileImageConfirm.heading"),
        message: t("header.accountSettings.removeProfileImageConfirm.message"),
        acceptLabel: t("buttons.delete"),
        rejectLabel: t("buttons.cancel"),
        accept: () => {
            resetImageCrop();
            imageUrl.value = "";
        }
    });
};

const avatarClicked = (event: Event) => {
    if (isMobileView()) {
        mobileAvatarMenu.value.toggle(event);
        return;
    }
    cropOverlay.value.show(event, globalThis.document.getElementById("userAvatarPreview"));
};

// logo
const logoMenu = ref();
const logoOptions = [
    {
        label: () => t("common.delete"),
        icon: "pi pi-trash",
        command: () => {
            logoUrl.value = "";
        }
    }
];
const logoClicked = (event: Event) => {
    if (trainer.value.logo || (logoUrl.value && logoUrl.value !== "")) {
        logoMenu.value.toggle(event);
    }
};

// logo preview tile: choosing a new logo or deleting the existing one
const previewLogoMenu = ref();
const previewLogoOptions = [
    {
        label: () => t("header.accountSettings.chooseNewTrainerLogo"),
        icon: "pi pi-image",
        command: () => triggerLogoSelect()
    },
    {
        label: () => t("common.delete"),
        icon: "pi pi-trash",
        command: () => {
            logoUrl.value = "";
        }
    }
];
const editLogoClicked = (event: Event) => {
    previewLogoMenu.value.toggle(event);
};

const normalizeWebsiteField = () => {
    website.value = normalizeWebsiteUrl(website.value);
    websiteFocused.value = false;
};

const onWebsiteFocus = () => {
    websiteFocused.value = true;
};

async function onSave() {
    if (!(props.trainer && firstname.value && lastname.value && email.value && distributor.value)) {
        return;
    }
    // Switching the International Partner has side effects (the backend disables monthly
    // billing), so a non-distributor account must confirm the change before it is saved.
    if (!distributorAccount.value && distributor.value.id !== props.trainer.distributor.id) {
        confirmDistributorChange();
        return;
    }
    await performSave();
}

function confirmDistributorChange() {
    confirm.require({
        group: "change-distributor",
        header: t("trainer-settings.distributor.confirmation.header"),
        message: t("trainer-settings.distributor.confirmation.message", {
            distributor: distributor.value?.company
        }),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            id: "account-view_btn_distributor-change-cancel",
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true
        },
        acceptProps: {
            id: "account-view_btn_distributor-change-confirm",
            label: t("buttons.ok")
        },
        accept: () => {
            performSave();
        }
    });
}

async function performSave() {
    if (!(props.trainer && firstname.value && lastname.value && email.value && distributor.value)) {
        return;
    }
    loading.value = true;
    try {
        let address: Address | null = null;
        if (street.value || zipCode.value || city.value || country.value || website.value || phoneNo.value) {
            address = {
                street: street.value ?? "",
                postalCode: zipCode.value ?? "",
                city: city.value ?? "",
                country: country.value ?? "",
                website: normalizeWebsiteUrl(website.value),
                phoneNo: phoneNo.value ?? "",
                id: props.trainer.address?.id ?? 0
            };
        }

        const portrait = croppedImage.value ? croppedImage.value.imageSrc : (imageUrl.value ?? "");
        const portraitChanged = portrait !== (initialPortrait.value ?? "");

        const updatedTrainer: Trainer = {
            id: props.trainer.id,
            firstname: firstname.value,
            lastname: lastname.value,
            email: email.value,
            company: company.value ?? "",
            recipient: selectedRecipient.value.value,
            language: selectedLanguage.value.value,
            distributor: distributor.value,
            address: address,
            timeZone: timeZone.value,
            isDistributorAccount: distributorAccount.value,
            purchaseLimit: props.trainer.purchaseLimit,
            isStockKeepingDistributor: props.trainer.isStockKeepingDistributor,
            portrait: portrait,
            logo: logoUrl.value,
            beta: props.trainer.beta,
            standardRetentionPeriod: formatMonthPeriod(standardParticipantRetentionPeriod.value),
            deletionNotification: standardParticipantDeletionNotification.value,
            // carried through explicitly: this PUT overwrites the whole row, so omitting it would
            // reset the release-notes marker and pop the features up again on the next login
            lastSeenReleaseNotesAt: props.trainer.lastSeenReleaseNotesAt
        };
        await ProfileAdministrationAPI.updateTrainerDetails(updatedTrainer);

        // Only write when the toggle actually changed
        if (distributorAccount.value &&
            isMonthlyBillingEnabledByDefault.value !== initialMonthlyBillingEnabledByDefault.value) {
            await ProfileAdministrationAPI.setMonthlyBillingDefaultForDistributor(
                distributor.value.id,
                isMonthlyBillingEnabledByDefault.value
            );
            initialMonthlyBillingEnabledByDefault.value = isMonthlyBillingEnabledByDefault.value;
        }

        if (portraitChanged) {
            emit("update:picture", updatedTrainer.portrait);
        }
        if (isNewAccount()) {
            localStorage.setItem("newAccount", "false");
        }

        // Update trainer details immediately so the change is reflected app-wide.
        trainerDetails.value = await getTrainerDetails();

        // Committed: clear staged crop state and re-baseline so the dialog is no
        // longer considered dirty, then close.
        resetImageCrop();
        initialPortrait.value = imageUrl.value;
        initialLogo.value = logoUrl.value;
        visible.value = false;
    } catch (error) {
        console.error("Failed to save account settings:", error);
    } finally {
        loading.value = false;
    }
}

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    }
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
        if (props.visibleToggle) {
            trainer.value = props.trainer;
            // Discard any staged-but-unsaved logo change from a previous open.
            logoUrl.value = props.trainer.logo ?? "";
        }
        resetImageCrop(true);
        // Re-baseline the picture/logo dirty check whenever the dialog (re)opens.
        initialPortrait.value = imageUrl.value;
        initialLogo.value = logoUrl.value;
    }
);

// --- Close / cancel guard for unsaved picture & logo changes -----------------

// The Dialog's built-in close (X), Esc and the footer Cancel button all route
// through here so they can be intercepted when there are unsaved image changes.
function handleVisibleUpdate(value: boolean) {
    if (value) {
        visible.value = true;
        return;
    }
    attemptClose();
}

function attemptClose() {
    if (hasUnsavedImageChanges.value) {
        showUnsavedConfirm.value = true;
    } else {
        visible.value = false;
    }
}

function discardChangesAndClose() {
    showUnsavedConfirm.value = false;
    imageUrl.value = initialPortrait.value;
    logoUrl.value = initialLogo.value;
    croppedImage.value = null;
    croppedImageCoordinates.value = null;
    visible.value = false;
}

function saveFromUnsavedPrompt() {
    showUnsavedConfirm.value = false;
    onSave();
}

// Warn on full page reload / tab close while the dialog has unsaved image changes.
const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (visible.value && hasUnsavedImageChanges.value) {
        event.preventDefault();
        event.returnValue = "";
    }
};

async function getLanguages(): Promise<{ name: string; value: string }[]> {
    const productLanguages = await LanguageManagementAPI.fetchLanguages();
    const uniqueByCode = new Map<string, { name: string; value: string }>();

    for (const product of productLanguages) {
        for (const code of product.languages) {
            if (uniqueByCode.has(code)) {
                continue;
            }

            uniqueByCode.set(code, {
                name: getLanguage(code) as string,
                value: code,
            });
        }
    }

    return Array.from(uniqueByCode.values()).sort((a, b) => a.name.localeCompare(b.name));
}

async function initializeTrainer() {
    distributors.value = await getDistributors();
    languages.value = await getLanguages();
    firstname.value = trainer.value?.firstname;
    lastname.value = trainer.value?.lastname;
    email.value = trainer.value?.email;
    company.value = trainer.value?.company;
    recipient.value = trainer.value?.recipient;
    selectedRecipient.value = {
        name: getRecipientName(props.trainer, trainer.value?.recipient ?? Recipients.TRAINER),
        value: trainer.value?.recipient ?? Recipients.TRAINER
    };
    selectedLanguage.value = {
        name: getLanguage(trainer.value?.language ?? "en"),
        value: trainer.value?.language ?? "en"
    };
    if (!isNewAccount()) {
        distributor.value = trainer.value?.distributor;
    }
    distributorAccount.value = trainer.value?.isDistributorAccount ?? false;
    timeZone.value = trainer.value?.timeZone ?? getCurrentTimeZone();
    imageUrl.value = trainer.value?.portrait ?? "";
    logoUrl.value = trainer.value?.logo ?? "";
    street.value = trainer.value?.address?.street;
    zipCode.value = trainer.value?.address?.postalCode;
    country.value = trainer.value?.address?.country;
    city.value = trainer.value?.address?.city;
    website.value = normalizeWebsiteUrl(trainer.value?.address?.website);
    phoneNo.value = trainer.value?.address?.phoneNo;
    isMonthlyBillingEnabled.value = trainer.value?.purchaseLimit > 0;
    isMonthlyBillingEnabledByDefault.value = trainer.value.distributor.monthlyBillingEnabled;
    initialMonthlyBillingEnabledByDefault.value = trainer.value.distributor.monthlyBillingEnabled;

    // Baseline the picture/logo dirty check against the freshly loaded values.
    initialPortrait.value = imageUrl.value;
    initialLogo.value = logoUrl.value;
    resetImageCrop();
}

const footerPt = {
    footer: {
        style: "display: block !important"
    }
};

/**
 * Starts Keycloak's dedicated update-email flow instead of letting the account console swap the
 * address directly. Keycloak sends a confirmation link to the NEW address and applies the change
 * only once that link is clicked, so an address can never become an identity before its owner
 * proved they hold it - and the user is returned to this page afterwards (RDT-353).
 */
function changeEmail() {
    keycloak.login({
        action: "UPDATE_EMAIL",
        redirectUri: getSafeRedirect(globalThis.location.href)
    });
}

const keycloakSettingsLink = computed(() => {
    const realm = import.meta.env.VITE_APP_KEYCLOAK_REALM;
    if (realm === "dev") {
        return "https://auth.dev.persolog.com/realms/dev/account?referrer=profile-administration";
    }
    if (realm === "demo") {
        return "https://auth.persolog.com/realms/demo/account?referrer=profile-administration";
    }
    return "https://auth.persolog.com/realms/prod/account?referrer=profile-administration";
});

watch(
    () => trainer.value,
    async () => {
        await initializeTrainer();
    }
);

onMounted(async () => {
    globalThis.addEventListener("beforeunload", onBeforeUnload);
    await initializeTrainer();
});

onUnmounted(() => {
    globalThis.removeEventListener("beforeunload", onBeforeUnload);
});
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="handleVisibleUpdate"
        id="account-view_dialog_account-settings"
        modal
        header="Header"
        :style="{ minWidth: '50vw', maxWidth: '1600px' }"
        :breakpoints="{ '1199px': '90vw', '575px': '97vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        :pt="footerPt"
    >
        <template #header>
            <div class="inline-flex items-center justify-between w-full">
                <div class="inline-flex items-center gap-2">
                    <Button id="logoPreview" type="button" link @click="logoClicked" class="p-0" untyled :aria-label="t('accessibility.buttons.chooseLogo')">
                        <Image class="h-8 w-auto" v-if="logoUrl != ''" :src="logoUrl" :alt="t('accessibility.images.logo')" width="auto" height="32" />
                    </Button>
                    <Menu ref="logoMenu" :model="logoOptions" :popup="true" @focus="autofocusFix" />

                    <Button id="userAvatarPreview" type="button" link @click="avatarClicked" class="p-0" untyled :aria-label="t('accessibility.buttons.chooseAvatar')">
                        <Preview
                            v-if="croppedImage"
                            ref="cropPreview"
                            :width="32"
                            :height="32"
                            :image="croppedImage.image"
                            :coordinates="croppedImageCoordinates"
                            :style="{ borderRadius: '100%' }"
                        />
                        <UserAvatar v-else :trainer="trainer" :image-url="imageUrl" size="normal" />
                    </Button>
                    <!-- Mobile only: avatar menu + its file input (the preview tiles and their input aren't rendered on mobile). -->
                    <template v-if="isMobileView()">
                        <input
                            id="account-view_file-upload_profile-image-mobile"
                            ref="portraitFileInputMobile"
                            type="file"
                            accept="image/*"
                            class="hidden-file-input"
                            @change="onPortraitFileChange"
                        />
                        <Menu ref="mobileAvatarMenu" :model="mobileAvatarOptions" :popup="true" @focus="autofocusFix" />
                        <ConfirmDialog group="removePortrait" :style="{ width: '100%' }" />
                    </template>

                    <span class="font-bold whitespace-nowrap">{{ trainer?.firstname }} {{ trainer?.lastname }}</span>
                </div>
                <AccountDeletion v-if="!isMobileView()" :trainer="trainer" />
            </div>
        </template>
        <!-- Personal information & account settings: two side-by-side blocks on md+,
             stacked on small screens. -->
        <div class="mt-4 grid grid-cols-12 gap-4">
            <!-- Personal information -->
            <div class="col-span-12 md:col-span-5">
                <Divider align="left" class="mt-0">
                    <b>{{ t("header.accountSettings.personalInformation") }}</b>
                </Divider>
                <div class="flex flex-col gap-6 mt-6">
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_firstname"
                                type="text"
                                v-model="firstname"
                                :value="firstname"
                                :placeholder="t('trainer-settings.firstname.placeholder')"
                                disabled
                                class="w-full"
                            />
                            <label for="account-view_input_firstname">{{ t("trainer-settings.firstname.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_lastname"
                                type="text"
                                v-model="lastname"
                                :value="lastname"
                                :placeholder="t('trainer-settings.lastname.placeholder')"
                                disabled
                                class="w-full"
                            />
                            <label for="account-view_input_lastname">{{ t("trainer-settings.lastname.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_email"
                                type="email"
                                v-model="email"
                                :value="email"
                                :placeholder="t('trainer-settings.email.placeholder')"
                                disabled
                                aria-describedby="account-help"
                                class="w-full"
                            />
                            <label for="account-view_input_email">{{ t("trainer-settings.email.label") }}</label>
                            <div id="account-help" class="flex justify-end items-center gap-3 mt-1">
                                <Button
                                    id="account-view_btn_change-email"
                                    :label="t('trainer-settings.changeEmail')"
                                    link
                                    size="small"
                                    class="p-0"
                                    @click="changeEmail"
                                />
                                <span class="text-muted-color" aria-hidden="true">|</span>
                                <Button
                                    id="account-view_link_keycloak"
                                    as="a"
                                    :href="keycloakSettingsLink"
                                    :label="t('trainer-settings.changeName')"
                                    link
                                    size="small"
                                    class="p-0"
                                />
                            </div>
                        </FloatLabel>
                    </div>

                    <!-- International partner (distributor) -->
                    <div class="mb-4 m-0 flex gap-2">
                        <FloatLabel v-if="!distributorAccount" class="w-full">
                            <Select
                                v-if="!distributorAccount"
                                v-model="distributor"
                                :options="distributors"
                                option-label="company"
                                :placeholder="t('trainer-settings.distributor.placeholder')"
                                filter
                                id="account-view_drop-down_distributors"
                                class="w-full"
                                :invalid="!distributor"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center">
                                        <div>{{ slotProps.value.company }}</div>
                                    </div>
                                    <span v-else>{{ slotProps.placeholder }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center">
                                        <div>{{ slotProps.option.company }}</div>
                                    </div>
                                </template>
                            </Select>
                            <label v-if="!distributorAccount" for="account-view_drop-down_distributors">
                                {{ distributorAccount ? trainer.distributor.company : t("trainer-settings.distributor.label") }}
                            </label>
                        </FloatLabel>
                        <FloatLabel v-else class="w-full">
                            <label v-if="distributorAccount" for="account-view_input_distributor">
                                {{ t("trainer-settings.distributor.label") }}
                            </label>
                            <InputText
                                v-if="distributorAccount"
                                id="account-view_input_distributor"
                                type="text"
                                v-model="trainer.distributor.company"
                                :value="trainer.distributor.company"
                                class="w-full"
                                :disabled="true"
                                readonly
                            />
                        </FloatLabel>
                        <TooltipButton
                            v-if="!distributorAccount"
                            id="account-view_tooltip-button_distributor"
                            :content="distributorInfoText"
                            background-color="transparent"
                        />
                    </div>
                </div>
            </div>

            <!-- Account settings -->
            <div class="col-span-12 md:col-span-4">
                <Divider align="left" class="mt-0">
                    <b>{{ t("header.accountSettings.heading") }}</b>
                </Divider>
                <div class="flex flex-col gap-6 mt-6">
                    <!-- Standard PR -->
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <Select
                                v-model="selectedRecipient"
                                :options="recipients"
                                option-label="name"
                                :placeholder="t('trainer-settings.recipients.placeholder')"
                                filter
                                id="account-view_drop-down_recipients"
                                class="w-full"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center">
                                        <div>{{ slotProps.value.name }}</div>
                                    </div>
                                    <span v-else>{{ slotProps.placeholder }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center">
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Select>
                            <label for="account-view_drop-down_recipients">{{ t("trainer-settings.recipients.label") }}</label>
                        </FloatLabel>
                    </div>

                    <!-- Standard PL -->
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <Select
                                v-model="selectedLanguage"
                                :options="languages"
                                option-label="name"
                                :placeholder="t('trainer-settings.language.placeholder')"
                                filter
                                id="account-view_drop-down_languages"
                                class="w-full"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center">
                                        <div>{{ slotProps.value.name }}</div>
                                    </div>
                                    <span v-else>{{ slotProps.placeholder }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center">
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Select>
                            <label for="account-view_drop-down_languages">{{ t("trainer-settings.language.label") }}</label>
                        </FloatLabel>
                    </div>

                    <!-- Timezone -->
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <Select
                                v-model="timeZone"
                                :options="timezones"
                                option-label="name"
                                :placeholder="t('trainer-settings.timezone.placeholder')"
                                filter
                                id="account-view_drop-down_timezone"
                                class="w-full"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center">
                                        <div>{{ slotProps.value }}</div>
                                    </div>
                                    <span v-else>{{ slotProps.placeholder }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center">
                                        <div>{{ slotProps.option }}</div>
                                    </div>
                                </template>
                            </Select>
                            <label for="account-view_drop-down_timezone">{{ t("trainer-settings.timezone.label") }}</label>
                        </FloatLabel>
                    </div>

                    <!-- Billing (distributor accounts only) -->
                    <div v-if="distributorAccount" class="mb-4 m-auto flex items-center gap-4">
                        <label for="account-view_toggle-switch_monthly-billing" class="text-sm m-0">
                            {{ t(`trainer-settings.monthlyBilling.label`) }}
                        </label>
                        <ToggleSwitch
                            v-model="isMonthlyBillingEnabledByDefault"
                            on-icon="pi pi-check"
                            off-icon="pi pi-times"
                            aria-describedby="distributorAccount"
                            id="account-view_toggle-switch_monthly-billing"
                            class="shrink-0"
                        />
                        <TooltipButton
                            v-if="distributorAccount"
                            id="account-view_tooltip-button_distributor-account"
                            :content="t('trainer-settings.monthlyBilling.tooltip')"
                            background-color="transparent"
                        />
                    </div>
                </div>
            </div>

            <!-- Standard data deletion -->
            <div class="col-span-12 md:col-span-3">
                <Divider align="left" class="mt-0">
                    <b>{{ t("header.accountSettings.dataRetention.heading") }}</b>
                    <TooltipButton :content="t('header.accountSettings.dataRetention.tooltip')" background-color="white" />
                </Divider>
                <div class="flex flex-col gap-6 mt-6 items-center">
                    <!-- Retention period -->
                    <div class="mb-4 m-0">
                        <FloatLabel>
                            <InputNumber
                                v-model="standardParticipantRetentionPeriod"
                                button-layout="horizontal"
                                show-buttons
                                :min="1"
                                :max="36"
                                :id="`project-config_input-number_retention-period-${trainer?.id ?? 0}`"
                                class="input-box-account-view"
                                :suffix="
                                    ' ' +
                                    t('project-configuration.retentionPeriod.value', {
                                        count: standardParticipantRetentionPeriod,
                                    })
                                "
                            />
                            <label :for="`project-config_input-number_retention-period-${trainer?.id ?? 0}`" class="m-0">{{
                                    t("header.accountSettings.dataRetention.monthPeriod")
                                }}</label>
                        </FloatLabel>
                    </div>

                    <!-- Participant deletion notification.
                         Between md and lg the column is cramped, so stack the toggle under
                         its label (md:flex-column) to give the text room; back to a row at lg. -->
                    <div class="mb-4 m-0 flex items-center md:flex-col md:items-start lg:flex-row lg:items-center gap-4 retention-toggle">
                        <label
                            :for="`project-config_input-switch_retention-notification-${trainer?.id ?? 0}`"
                            class="text-sm m-0"
                        >
                            {{ t("header.accountSettings.dataRetention.retentionNotification.heading") }}
                        </label>
                        <ToggleSwitch
                            :id="`project-config_input-switch_retention-notification-${trainer?.id ?? 0}`"
                            v-model="standardParticipantDeletionNotification"
                            class="shrink-0"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Address & preview: side by side on md+, stacked on small screens -->
        <div class="mt-4 grid grid-cols-12 gap-4">
            <!-- Address -->
            <div class="col-span-12 lg:col-span-6 md:col-span-12">
                <Divider align="left" class="mt-0">
                    <b>{{ t("header.accountSettings.businessCardInfo") }}</b>
                </Divider>
                <Message severity="info" :closable="false" size="small">
                    {{ t("trainer-settings.address.info") }}
                </Message>
                <div class="mt-8 grid grid-cols-12 gap-4">
                    <div class="col-span-12 md:col-span-6 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_company"
                                type="text"
                                v-model="company"
                                :value="company"
                                class="w-full"
                            />
                            <label for="account-view_input_company">{{ t("trainer-settings.company.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="col-span-12 md:col-span-6 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_street"
                                type="text"
                                v-model="street"
                                :value="street"
                                class="w-full"
                                autocomplete="address-line1"
                            />
                            <label for="account-view_input_street">{{ t("trainer-settings.street.label") }}</label>
                        </FloatLabel>
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-12 gap-4">
                    <div class="col-span-12 md:col-span-3 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_zipCode"
                                type="text"
                                v-model="zipCode"
                                :value="zipCode"
                                class="w-full"
                                autocomplete="postal-code"
                            />
                            <label for="account-view_input_zipCode">{{ t("trainer-settings.zipCode.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="col-span-12 md:col-span-5 mb-4">
                        <FloatLabel>
                            <InputText id="account-view_input_city" type="text" v-model="city" :value="city" class="w-full" />
                            <label for="account-view_input_city">{{ t("trainer-settings.city.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="col-span-12 md:col-span-4 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_country"
                                type="text"
                                v-model="country"
                                :value="country"
                                class="w-full"
                                autocomplete="country-name"
                            />
                            <label for="account-view_input_country">{{ t("trainer-settings.country.label") }}</label>
                        </FloatLabel>
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-12 gap-4">
                    <div class="col-span-12 md:col-span-6 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_phoneNo"
                                type="text"
                                v-model="phoneNo"
                                :value="phoneNo"
                                class="w-full"
                                autocomplete="tel"
                            />
                            <label for="account-view_input_phoneNo">{{ t("trainer-settings.phoneNo.label") }}</label>
                        </FloatLabel>
                    </div>
                    <div class="col-span-12 md:col-span-6 mb-4">
                        <FloatLabel>
                            <InputText
                                id="account-view_input_website"
                                type="text"
                                v-model="website"
                                :value="website"
                                :placeholder="websiteFocused ? 'https://' : ''"
                                @focus="onWebsiteFocus"
                                @blur="normalizeWebsiteField"
                                class="w-full"
                                autocomplete="url"
                            />
                            <label for="account-view_input_website">{{ t("trainer-settings.website.label") }}</label>
                        </FloatLabel>
                    </div>
                </div>
            </div>

            <!-- Preview -->
            <div class="col-span-12 lg:col-span-6 md:col-span-12" v-if="!isMobileView()">
                <Divider align="left" class="mt-0">
                    <b>{{ t("header.accountSettings.preview") }}</b>
                </Divider>
                <div class="flex flex-wrap gap-6 mt-6 justify-center">
                    <!-- Logo -->
                    <div class="flex flex-col items-center gap-2">
                        <input
                            id="account-view_file-upload_logo"
                            ref="logoFileInput"
                            type="file"
                            accept="image/*"
                            class="hidden-file-input"
                            @change="onLogoFileChange"
                        />
                        <!-- Existing logo: hover to edit (choose new / delete) -->
                        <button
                            v-if="logoUrl !== ''"
                            type="button"
                            id="account-view_tile_logo"
                            class="logo-placeholder image-edit-tile"
                            :aria-label="t('header.accountSettings.editTrainerLogo')"
                            @click="editLogoClicked"
                        >
                            <img
                                id="account-view_image_logo"
                                :src="logoUrl"
                                :alt="t('accessibility.images.logo', { company: company })"
                                class="tile-image tile-image--contain"
                            />
                            <div class="tile-overlay">
                                <i class="pi pi-pencil"></i>
                                <span>{{ t("header.accountSettings.editTrainerLogo") }}</span>
                            </div>
                        </button>
                        <!-- Empty logo placeholder: click to choose -->
                        <button
                            v-else
                            type="button"
                            id="account-view_tile_logo-empty"
                            class="logo-placeholder image-edit-tile image-edit-tile--empty"
                            :aria-label="t('accessibility.buttons.chooseLogo')"
                            @click="triggerLogoSelect"
                        >
                            <i class="pi pi-plus tile-plus"></i>
                            <span class="tile-empty-label">{{ t("header.accountSettings.chooseTrainerLogo") }}</span>
                        </button>
                        <Menu ref="previewLogoMenu" :model="previewLogoOptions" :popup="true" @focus="autofocusFix" />
                        <div class="flex items-center gap-1">
                            <span>{{ t("header.accountSettings.logo") }}</span>
                            <TooltipButton
                                id="account-view_tooltip-button_logo-upload"
                                :content="t('trainer-settings.upload.info')"
                                background-color="transparent"
                            />
                        </div>
                    </div>

                    <!-- Business card: profile picture + contact data, next to each other -->
                    <div class="flex flex-col items-center gap-2">
                        <div class="flex gap-2">
                            <!-- Picture -->
                            <input
                                id="account-view_file-upload_profile-image"
                                ref="portraitFileInput"
                                type="file"
                                accept="image/*"
                                class="hidden-file-input"
                                @change="onPortraitFileChange"
                            />
                            <!-- Existing picture: hover to edit (opens cropper with delete option) -->
                            <button
                                v-if="imageUrl !== ''"
                                type="button"
                                id="account-view_tile_portrait"
                                class="profile-image image-edit-tile"
                                :aria-label="t('header.accountSettings.editProfileImage')"
                                @click="editPortraitClicked"
                            >
                                <img
                                    id="account-view_image_portrait"
                                    :src="imageUrl"
                                    class="tile-image tile-image--cover"
                                    :alt="t('accessibility.images.userAvatar')"
                                />
                                <div class="tile-overlay">
                                    <i class="pi pi-pencil"></i>
                                    <span>{{ t("header.accountSettings.editProfileImage") }}</span>
                                </div>
                            </button>
                            <!-- Empty picture placeholder: click to choose -->
                            <button
                                v-else
                                type="button"
                                id="account-view_tile_portrait-empty"
                                class="portrait-placeholder image-edit-tile image-edit-tile--empty"
                                :aria-label="t('accessibility.buttons.chooseAvatar')"
                                @click="triggerPortraitSelect"
                            >
                                <i class="pi pi-plus tile-plus"></i>
                                <span class="tile-empty-label">{{ t("header.accountSettings.chooseProfileImage") }}</span>
                            </button>

                            <div class="business-card-text-box" id="account-view_div_contact-card">
                                <div>
                                    <strong>{{ t("header.accountSettings.contact.sub") }}</strong>
                                </div>
                                <div>{{ fullname }}</div>
                                <div>{{ company }}</div>
                                <div>{{ street }}</div>
                                <div>{{ zipCode }} {{ city }}</div>
                                <div>{{ country }}</div>
                                <div>{{ phoneNo }}</div>
                                <div>{{ website }}</div>
                            </div>
                        </div>
                        <div>{{ t("header.accountSettings.contact.heading") }}</div>
                    </div>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 mt-8" :pt="footerPt">
                <Popover
                    v-if="!isMobileView()"
                    ref="cropOverlay"
                    :show-close-icon="true"
                    :dismissable="false"
                    :close-on-escape="false"
                >
                    <div class="image-edit-container">
                        <div class="image-edit-content">
                            <div class="cropper-container">
                                <Cropper
                                    ref="cropper"
                                    :src="imageUrl"
                                    :width="300"
                                    :height="300"
                                    :canvas="{
                                        minHeight: 48,
                                        minWidth: 48,
                                        maxHeight: 1024,
                                        maxWidth: 1024,
                                    }"
                                    :default-position="
                                        ({ coordinates, imageSize }: any) => {
                                            return {
                                                left: imageSize.width / 2 - coordinates.width / 2,
                                                top: 0,
                                            };
                                        }
                                    "
                                    :default-size="
                                        ({ imageSize }: any) => {
                                            return {
                                                width: imageSize.width,
                                                height: imageSize.height,
                                            };
                                        }
                                    "
                                    default-boundaries="fill"
                                    :stencil-component="CircleStencil"
                                    :stencil-props="{ aspectRatio: 1 }"
                                    :debounce="false"
                                    @change="onCropChanged"
                                />
                            </div>
                            <!-- Business Card Preview in Cropper-->
                            <div class="cropper-business-card-preview">
                                <div class="cropper-preview-label">{{ t('header.accountSettings.contact.heading') }}</div>
                                <div class="cropper-business-card-container">
                                <div class="profile-image-preview">
                                    <img
                                        v-if="croppedImage"
                                        :src="croppedImage.imageSrc"
                                        :alt="trainer?.firstname + ' ' + trainer?.lastname"
                                        class="cropper-business-card-image"
                                    />
                                    <div v-else class="preview-placeholder">Profile Preview</div>
                                </div>
                                <div class="contact-info-preview">
                                    <div class="contact-text">
                                        <strong>{{ t("header.accountSettings.contact.sub") }}</strong>
                                    </div>
                                    <div class="contact-text">{{ fullname }}</div>
                                    <div class="contact-text">{{ company }}</div>
                                    <div class="contact-text">{{ street }}</div>
                                    <div class="contact-text">{{ zipCode }} {{ city }}</div>
                                    <div class="contact-text">{{ country }}</div>
                                    <div class="contact-text">{{ phoneNo }}</div>
                                    <div class="contact-text">{{ website }}</div>
                                </div>
                            </div>
                            </div>
                        </div>
                        <!-- Action Buttons -->
                        <div class="cropper-actions">
                            <Button
                                id="account-view_btn_crop-cancel"
                                :label="t('buttons.cancel')"
                                @click="onCropCancel"
                                size="small"
                                outlined
                            />
                            <Button
                                v-if="imageUrl !== ''"
                                id="account-view_btn_delete-portrait"
                                :label="t('header.accountSettings.removeProfileImage')"
                                icon="pi pi-trash"
                                @click="onCropDelete"
                                size="small"
                                severity="danger"
                                outlined
                            />
                            <Button
                                id="account-view_btn_choose-new-portrait"
                                :label="t('header.accountSettings.chooseProfileImage')"
                                icon="pi pi-image"
                                @click="triggerPortraitSelect"
                                size="small"
                                outlined
                            />
                            <Button
                                id="account-view_btn_crop-save"
                                :label="t('buttons.save')"
                                @click="onCropConfirm"
                                size="small"
                                :disabled="!croppedImage"
                            />
                        </div>
                    </div>
                </Popover>

                <div class="flex gap-2">
                    <AccountDeletion v-if="isMobileView()" :trainer="trainer" />
                    <Button id="account-view_btn_cancel" :label="t('buttons.cancel')" @click="attemptClose" size="small" outlined></Button>
                    <Button
                        id="account-view_btn_save"
                        :disabled="!distributor"
                        :label="t('buttons.save')"
                        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
                        @click="onSave"
                        size="small"
                    />
                </div>
            </div>
        </template>
    </Dialog>

    <!-- Confirmation shown before saving a changed International Partner (non-distributor accounts). -->
    <ConfirmDialog group="change-distributor" id="account-view_dialog_change-distributor">
        <template #message>
            <div class="flex flex-col gap-2">
                <p id="account-view_text_distributor-change-message" class="m-0">
                    {{ t("trainer-settings.distributor.confirmation.message", { distributor: distributor?.company }) }}
                </p>
                <p v-if="isMonthlyBillingEnabled" id="account-view_text_monthly-billing-hint" class="m-0 font-bold">
                    {{ t("trainer-settings.distributor.confirmation.monthlyBillingHint") }}
                </p>
            </div>
        </template>
    </ConfirmDialog>

    <!-- Guard prompt shown when closing/cancelling with unsaved picture or logo changes. -->
    <Dialog
        v-model:visible="showUnsavedConfirm"
        id="account-view_dialog_unsaved-changes"
        modal
        :draggable="false"
        :closable="false"
        :style="{ width: '32rem' }"
        :breakpoints="{ '575px': '90vw' }"
        :header="t('header.accountSettings.unsavedChanges.heading')"
    >
        <p class="m-0">{{ t("header.accountSettings.unsavedChanges.message") }}</p>
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    id="account-view_btn_unsaved-keep-editing"
                    :label="t('header.accountSettings.unsavedChanges.keepEditing')"
                    @click="showUnsavedConfirm = false"
                    size="small"
                    text
                />
                <Button
                    id="account-view_btn_unsaved-discard"
                    :label="t('header.accountSettings.unsavedChanges.discard')"
                    icon="pi pi-trash"
                    severity="danger"
                    outlined
                    @click="discardChangesAndClose"
                    size="small"
                />
                <Button
                    id="account-view_btn_unsaved-save"
                    :label="t('header.accountSettings.unsavedChanges.save')"
                    icon="pi pi-check"
                    @click="saveFromUnsavedPrompt"
                    size="small"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.image-edit-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
}

.image-edit-content {
    display: flex;
    justify-content: space-around;
    align-items: flex-start;
    gap: 30px;
    width: 100%;
}

.cropper-container {
    flex: 1;
    max-width: 300px;
    max-height: 500px;
}

.cropper-business-card-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.cropper-preview-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--p-text-secondary);
    text-align: center;
}

.cropper-business-card-container {
    display: flex;
    gap: 12px;
    padding: 16px;
    border: 1px solid var(--p-primary-100);
    border-radius: 8px;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    max-width: 400px;
    height: 280px;
}

.profile-image-preview {
    width: 187.5px;
    height: 250px;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
}

.profile-image {
    width: 187.5px;
    height: 250px;
    border: none;
    object-fit: cover;
    object-position: center;
}


.cropper-business-card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.cropper-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.contact-info-preview {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 4px;
    padding-top: 8px;
}

.contact-text {
    font-size: 0.75rem;
    color: var(--p-text-secondary);
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.preview-placeholder {
    color: lightgray;
    font-size: 0.75rem;
    text-align: center;
}

.business-card-text-box {
    width: 300px;
    height: 250px;
    border: 1px solid black;
    padding: 10px;
    box-sizing: border-box;
    font-size: 0.9rem;
}

.portrait-placeholder {
    width: 187.5px;
    height: 250px;
    border: 1px solid black;
    padding: 10px;
    box-sizing: border-box;
}

.logo-placeholder {
    width: 200px;
    height: 200px;
    border: 1px solid black;
    padding: 10px;
    box-sizing: border-box;
}

/* Interactive logo/portrait preview tiles */
.hidden-file-input {
    display: none;
}

.image-edit-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0;
    background: transparent;
    appearance: none;
    font: inherit;
    color: var(--p-text-secondary);
    cursor: pointer;
    overflow: hidden;
    box-sizing: border-box;
}

.image-edit-tile--empty {
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.image-edit-tile--empty:hover,
.image-edit-tile--empty:focus-visible {
    background-color: var(--p-primary-50);
    border-color: var(--p-primary-color);
    color: var(--p-primary-color);
}

.tile-plus {
    font-size: 2.5rem;
}

.tile-empty-label {
    font-size: 0.9rem;
    text-align: center;
}

.tile-image {
    width: 100%;
    height: 100%;
}

.tile-image--contain {
    object-fit: contain;
}

.tile-image--cover {
    object-fit: cover;
    object-position: center;
}

.tile-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 0.9rem;
    opacity: 0;
    transition: opacity 0.15s ease;
}

.tile-overlay i {
    font-size: 1.75rem;
}

.image-edit-tile:hover .tile-overlay,
.image-edit-tile:focus-visible .tile-overlay {
    opacity: 1;
}

.big-text {
    font-size: 1.5rem;
    font-weight: bold;
}

.switch-label {
    top: var(--p-floatlabel-over-active-top);
    font-size: var(--p-floatlabel-active-font-size);
}

.custom-switch {
    margin-top: 20%;
}

/* Cap the WHOLE retention control (input + horizontal buttons) so it never grows
   past the narrow md:col-3 data-retention column
*/
.input-box-account-view {
    max-width: 200px;
}

.input-box-account-view :deep(.p-inputnumber-input) {
    min-width: 0;
}

/* Between md and lg the column is at its tightest (~25% of a 90vw dialog), so
   shrink the control further and reduce the suffix/label text so the whole
   " x Monate" value stays inside the column. */
@media (min-width: 768px) and (max-width: 991px) {
    .input-box-account-view {
        max-width: 118px;
    }

    .input-box-account-view :deep(.p-inputnumber-input) {
        font-size: 0.6rem;
        padding-inline: 0.2rem;
    }

    .input-box-account-view :deep(.p-inputnumber-button) {
        width: 1.4rem;
    }

    .retention-toggle label {
        font-size: 0.7rem;
    }
}

.p-dialog-footer {
    background-color: #c94141; /* Example: change background color */
    padding: 1rem; /* Example: add padding */
    display: block !important;
    text-align: right; /* Example: align text to the right */
}
</style>
